import React, { useMemo, useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Button, VStack, HStack, Box, Text, Select, Table, Thead, Tbody, Tr, Th, Td, Badge,
  Checkbox, Progress, Alert, AlertIcon, useToast, Input, Divider, RadioGroup, Radio,
} from '@chakra-ui/react';
import { parseFileData, getSheetRows } from '../utils/patientImport/parseFile';
import { detectHeader } from '../utils/patientImport/detectHeader';
import { matchColumns, detectAddressParts } from '../utils/patientImport/matchColumns';
import {
  emptySpec, isSpecMapped, addSource, removeSource, setSource, setSeparator, deriveMappingInputs, buildSeed,
} from '../utils/patientImport/mappingInputs';
import { transformRows } from '../utils/patientImport/transformRows';
import { findInFileDuplicates, fetchExistingKeys, findExistingDuplicates, fetchExistingRows, keyForRow } from '../utils/patientImport/findDuplicates';
import { classifyDifferences, buildMergedRecord } from '../utils/patientImport/mergeRecords';
import { commitImport } from '../utils/patientImport/commitImport';
import { addColumns } from '../utils/patientImport/addColumns';
import { fetchTableColumns } from '../utils/patientImport/fetchTableColumns';
import { getBackendURL } from './env';
import { runPatientTableJob, PATIENT_TABLE_CONFIRMATION, PATIENT_TABLE_EMPTY_CONFIRMATION } from '../utils/patientTableJob';
import { saveRecentImport } from '../utils/patientImport/recentImport';
import { getTarget, DEFAULT_TARGET, TARGET_TABLES } from '../utils/patientImport/targetTables';
import { TARGET_FIELDS, NAME_PART_KEYS } from '../utils/patientImport/fieldSynonyms';
import { NAME_FORMATS } from '../utils/patientImport/coerce';
import { sanitizeColumnName, RESERVED_COLUMNS } from '../utils/patientImport/columnName';
import { isImpersonating } from '../utils/impersonation';
import { supabaseSchema } from './supabaseClient';

const REQUIRED_FIELD_KEYS = new Set(TARGET_FIELDS.filter((f) => f.required).map((f) => f.key));
const FIELD_LABEL = Object.fromEntries(TARGET_FIELDS.map((f) => [f.key, f.label]));
// Special columns get invisible parsing (phones -> array, dates -> ISO, name
// assembly). This just labels that in the UI so the user knows what happens.
const FIELD_PARSE_HINT = {
  patient_name: 'name', tel_num: 'phone', date_: 'visit date', dob: 'date of birth', email: 'email', EHR_ID: 'patient ID',
};

function colLabel(headers, i) {
  const h = headers[i];
  return h != null && String(h).trim() ? String(h).trim() : `(Column ${i + 1})`;
}

const CONFIDENCE_COLOR = { high: 'green', medium: 'yellow', low: 'orange' };
const DATE_PAGE_SIZE = 50;
const PREVIEW_LIMIT = 8;
// After new columns are created, PostgREST reloads its schema cache asynchronously
// (the backend NOTIFYs it). Pause briefly before the first upsert so it lands after
// the reload — otherwise the first write races ahead and 400s with "Could not find
// the '<col>' column" (harmless: commitImport retries schema-cache errors, but the
// failed request still shows in the console). Only incurred when columns are added.
const PGRST_SCHEMA_SETTLE_MS = 1500;

// Render a record value the way it will be stored (arrays -> comma list, other
// objects -> JSON) for the pre-import preview table.
function formatPreviewValue(v) {
  if (v == null) return '';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// How combined source columns join into one value (mirrors the name "Combine as").
const SEPARATOR_OPTIONS = [
  { value: ', ', label: 'comma  (A, B)' },
  { value: ' ', label: 'space  (A B)' },
  { value: ' - ', label: 'dash  (A - B)' },
  { value: ' | ', label: 'pipe  (A | B)' },
];

export default function PatientImportModal({ isOpen, onClose, onImported }) {
  const toast = useToast();
  const blocked = isImpersonating();

  const [step, setStep] = useState(1);
  const [targetTable, setTargetTable] = useState(DEFAULT_TARGET);
  const [fileName, setFileName] = useState('');
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [rawRows, setRawRows] = useState([]);
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [dataStartRowIndex, setDataStartRowIndex] = useState(1);
  const [matchResult, setMatchResult] = useState({ columnMap: {}, confidence: {} }); // matchColumns output (fieldKey -> header idx)
  const [columnCombine, setColumnCombine] = useState({}); // real column name -> { sources:[idx...], separator }
  const [newColumns, setNewColumns] = useState([]); // user-added new columns: { id, name, sources, separator }
  const [nameFormat] = useState(NAME_FORMATS[0]);
  const [onCoerceFail, setOnCoerceFail] = useState('null');
  const [bulkDate, setBulkDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rowDates, setRowDates] = useState({});
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const [datePage, setDatePage] = useState(0);
  const [dedupKey, setDedupKey] = useState('EHR_ID');
  const [duplicateAction, setDuplicateAction] = useState('skip'); // 'skip' | 'merge' | 'replace' | 'both'
  const [existingRowsByKey, setExistingRowsByKey] = useState(() => new Map());
  const [duplicateDiff, setDuplicateDiff] = useState({ newColumns: [], changedColumns: [], changedRowCount: 0, identicalCount: 0 });
  const [existingDupes, setExistingDupes] = useState(new Set());
  const [checkingDupes, setCheckingDupes] = useState(false);
  const [showDupes, setShowDupes] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [tableColumns, setTableColumns] = useState(null); // real columns of target table; null = unknown
  const [columnsForTable, setColumnsForTable] = useState(null); // which table tableColumns belongs to
  const [columnsError, setColumnsError] = useState(null); // plain-English reason the column list couldn't load
  const [buildingTable, setBuildingTable] = useState(false);

  const target = getTarget(targetTable);

  const headers = useMemo(() => (rawRows[headerRowIndex] || []), [rawRows, headerRowIndex]);
  const columnOptions = useMemo(
    () => headers.map((_, i) => ({ value: i, label: colLabel(headers, i) })),
    [headers],
  );

  // Real columns of the selected table, once fetched. Only trust them when they
  // belong to the CURRENT target (on a target switch tableColumns briefly still
  // holds the previous table's columns). Null/empty => unknown.
  const columnSet = useMemo(
    () => (Array.isArray(tableColumns) && tableColumns.length && columnsForTable === target.table
      ? new Set(tableColumns) : null),
    [tableColumns, columnsForTable, target.table],
  );
  // Merge/Replace/Both duplicate handling is only possible when we know the table's
  // real columns (to classify new vs changed) AND the table has a conflict key to
  // upsert on. Otherwise duplicates are simply skipped (the default).
  const canMerge = !!columnSet && !!target.onConflict;
  // A live column list that came back empty means the table doesn't exist here
  // (a real table always has >= 1 column). Fetch errors throw -> tableColumns null.
  const tableMissing = columnsForTable === target.table
    && Array.isArray(tableColumns) && tableColumns.length === 0;

  // ONE uniform mapping list = this table's REAL columns (minus auto-managed
  // ones). Each entry { col: display + storage name, fieldKey: special parser or
  // null, label, required }. When columns can't be loaded we fall back to the
  // curated field list so the wizard still works.
  const displayColumns = useMemo(() => {
    if (!columnSet) {
      return TARGET_FIELDS.map((f) => ({ col: f.key, fieldKey: f.key, label: f.label, required: f.required }));
    }
    const spec = target.columnSpec || {};
    const managed = new Set(target.managedColumns || []);
    return tableColumns.filter((c) => !managed.has(c)).map((c) => ({
      col: c, fieldKey: spec[c] || null, label: c, required: REQUIRED_FIELD_KEYS.has(spec[c]),
    }));
  }, [columnSet, tableColumns, target]);

  // Seed the mapping from the auto-match + live columns: special columns take
  // their matched header (patient_name folds in split name parts), plain columns
  // auto-match a same-named header, and — mirroring the old importer — every
  // header NOT consumed by a real column surfaces as an auto-suggested new column
  // (with >=2 address parts collapsed into one combined "address"). User-added
  // ("nc_") columns are preserved; re-seeds only when the file/columns/target
  // change, so edits persist otherwise.
  useEffect(() => {
    const columnMap = matchResult.columnMap || {};
    const nameParts = NAME_PART_KEYS.map((k) => columnMap[k]).filter((v) => v != null);
    const addressPartIdx = Object.values(detectAddressParts(headers)).filter((v) => v != null);
    const { columnCombine: seeded, newColumns: auto } = buildSeed({
      displayColumns, headers, columnMap, nameParts, addressPartIdx, existingColumns: columnSet,
    });
    setColumnCombine(seeded);
    setNewColumns((prev) => [...auto, ...prev.filter((c) => String(c.id).startsWith('nc_'))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayColumns, matchResult, headers]);

  // Derive the transform inputs (columnMap for special parsers, extraColumns for
  // every plain/new column) from the unified combine state.
  const { columnMap, extraColumns: derivedExtraColumns, newColumnNames } = useMemo(
    () => deriveMappingInputs({ displayColumns, columnCombine, newColumns, existingColumns: columnSet }),
    [displayColumns, columnCombine, newColumns, columnSet],
  );

  const confidence = matchResult.confidence || {};
  const effectiveDedupKey = !isSpecMapped(columnMap.EHR_ID) && dedupKey === 'EHR_ID' ? 'name_dob' : dedupKey;

  // Load the target table's real columns when the modal opens or the target
  // changes. A failure leaves tableColumns null -> curated-list fallback.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const forTable = target.table;
    setTableColumns(null);
    setColumnsForTable(null);
    setColumnsError(null);
    setNewColumns([]);
    fetchTableColumns({ table: forTable, schema: supabaseSchema })
      .then((cols) => { if (!cancelled) { setTableColumns(cols); setColumnsForTable(forTable); setColumnsError(null); } })
      .catch((err) => { if (!cancelled) { setTableColumns(null); setColumnsForTable(null); setColumnsError(err.message || 'Could not load the table columns.'); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, target.table]);

  // Required columns (the table's own, or the curated required set in fallback)
  // that are still unmapped. A required special column routed through columnMap is
  // "filled" when its spec has a source; patient_name is filled by any name source.
  const requiredBlank = useMemo(() => {
    const label = (c) => c.label || FIELD_LABEL[c.fieldKey] || c.col;
    return displayColumns
      .filter((c) => c.required && !isSpecMapped(columnCombine[c.col]))
      .map(label);
  }, [displayColumns, columnCombine]);

  const transformed = useMemo(() => {
    if (!rawRows.length) return { rows: [], stats: { total: 0, imported: 0, skippedNoName: 0, unparseable: { dob: 0, date_: 0, tel_num: 0, home_phone: 0 } } };
    return transformRows({
      rawRows, dataStartRowIndex, columnMap, nameFormat,
      transforms: { parseDates: true, normalizePhones: true, onCoerceFail },
      extraColumns: derivedExtraColumns,
    });
  }, [rawRows, dataStartRowIndex, columnMap, nameFormat, onCoerceFail, derivedExtraColumns]);

  const inFileDupes = useMemo(
    () => findInFileDuplicates(transformed.rows, effectiveDedupKey),
    [transformed.rows, effectiveDedupKey],
  );

  const allDupes = useMemo(() => {
    const s = new Set(inFileDupes);
    existingDupes.forEach((i) => s.add(i));
    return s;
  }, [inFileDupes, existingDupes]);

  // Apply per-row visit dates: rowDates override the mapped date_; blank => roster entry (no date).
  const datedRows = useMemo(() => transformed.rows.map((r, i) => {
    const d = rowDates[i] !== undefined ? rowDates[i] : (r.date_ || '');
    const out = { ...r };
    if (d) out.date_ = d; else delete out.date_;
    return out;
  }), [transformed.rows, rowDates]);

  // Rows the current target can actually accept (patients_with_ehrid needs an
  // EHR ID). Keyed by ORIGINAL transformed-row index so per-row dates stay stable.
  const eligibleIndices = useMemo(
    () => transformed.rows.map((_, i) => i).filter((i) => target.isEligible(transformed.rows[i])),
    [transformed.rows, target],
  );
  const eligibleSet = useMemo(() => new Set(eligibleIndices), [eligibleIndices]);
  const excludedNoEhr = transformed.stats.imported - eligibleIndices.length;

  const extraColumnError = useMemo(() => {
    // Reserved / blank-name checks apply to user-added new columns; real table
    // columns are legitimate destinations by definition. A new column with no
    // sources yet is still being built, so don't flag its (maybe blank) name.
    for (const c of newColumns) {
      if (!isSpecMapped(c)) continue;
      const name = sanitizeColumnName(c.name);
      if (!name) return 'Every added column needs a valid name.';
      if (RESERVED_COLUMNS.has(name)) return `"${name}" is a reserved column — rename it.`;
    }
    const names = derivedExtraColumns.map((c) => c.name);
    if (new Set(names).size !== names.length) return 'Two columns resolve to the same name — rename one.';
    return null;
  }, [newColumns, derivedExtraColumns]);

  // --- Combine editors -------------------------------------------------------
  // Existing columns are keyed by real column name in columnCombine; user-added
  // ones live in the newColumns array. Both use the same immutable spec helpers.
  const editCombine = (col, fn) => setColumnCombine((m) => ({ ...m, [col]: fn(m[col] || emptySpec()) }));
  const addColSource = (col) => editCombine(col, addSource);
  const removeColSource = (col, slot) => editCombine(col, (s) => removeSource(s, slot));
  const setColSource = (col, slot, val) => editCombine(col, (s) => setSource(s, slot, val));
  const setColSeparator = (col, sep) => editCombine(col, (s) => setSeparator(s, sep));

  const newColIdRef = React.useRef(0);
  const addNewColumn = () => setNewColumns((list) => [
    ...list, { id: `nc_${(newColIdRef.current += 1)}`, name: '', sources: [null], separator: ', ' },
  ]);
  const removeNewColumn = (id) => setNewColumns((list) => list.filter((c) => c.id !== id));
  const editNewColumn = (id, fn) => setNewColumns((list) => list.map((c) => (c.id === id ? fn(c) : c)));
  const renameNewColumn = (id, name) => editNewColumn(id, (c) => ({ ...c, name }));
  const addNewSource = (id) => editNewColumn(id, addSource);
  const removeNewSource = (id, slot) => editNewColumn(id, (c) => removeSource(c, slot));
  const setNewSource = (id, slot, val) => editNewColumn(id, (c) => setSource(c, slot, val));
  const setNewSeparator = (id, sep) => editNewColumn(id, (c) => setSeparator(c, sep));

  // Live preview of a combined value, built from the first data row so the user
  // sees exactly what will be saved (mirrors the name "Combine as" preview).
  const previewCombine = (spec) => {
    const sample = rawRows[dataStartRowIndex] || [];
    return ((spec && spec.sources) || [])
      .map((i) => (i == null ? '' : String(sample[i] == null ? '' : sample[i]).trim()))
      .filter(Boolean)
      .join((spec && spec.separator) || ', ');
  };

  const reset = () => {
    setStep(1); setTargetTable(DEFAULT_TARGET); setFileName(''); setWorkbook(null); setSheetNames([]); setSelectedSheet('');
    setRawRows([]); setHeaderRowIndex(0); setDataStartRowIndex(1);
    setMatchResult({ columnMap: {}, confidence: {} }); setColumnCombine({}); setNewColumns([]);
    setExistingDupes(new Set()); setShowDupes(false);
    setImporting(false); setProgress({ done: 0, total: 0 }); setResult(null);
    setOnCoerceFail('null');
    setDedupKey('EHR_ID'); setCheckingDupes(false);
    setDuplicateAction('skip'); setExistingRowsByKey(new Map());
    setDuplicateDiff({ newColumns: [], changedColumns: [], changedRowCount: 0, identicalCount: 0 });
    setBulkDate(new Date().toISOString().slice(0, 10));
    setRowDates({}); setSelectedRows(new Set()); setDatePage(0);
    setTableColumns(null); setColumnsForTable(null); setColumnsError(null);
  };

  const handleClose = () => { reset(); onClose(); };

  // Same functionality as SuperAdmin's "Build patient tables" button: builds
  // patients_with_ehrid + patients_no_ehrid (with their RLS policies) from the
  // current schema's notes-c, then re-reads the target's columns.
  const buildPatientTables = async (empty = false) => {
    setBuildingTable(true);
    try {
      const result = await runPatientTableJob({ backendUrl: getBackendURL(), schema: supabaseSchema || 'public', empty });
      const withEhr = result?.inserted?.patients_with_ehrid ?? 0;
      const withoutEhr = result?.inserted?.patients_no_ehrid ?? 0;
      toast(empty
        ? { title: 'Empty patient tables created', description: 'Tables are ready — no data imported. You can map and import now.', status: 'success' }
        : { title: 'Patient tables built', description: `Inserted ${withEhr} EHR and ${withoutEhr} non-EHR patient(s).`, status: 'success' });
      try {
        const cols = await fetchTableColumns({ table: target.table, schema: supabaseSchema });
        setTableColumns(cols); setColumnsForTable(target.table);
      } catch { setTableColumns(null); setColumnsForTable(null); }
    } catch (err) {
      toast({ title: 'Build failed', description: err.message || 'Failed to build patient tables', status: 'error' });
    } finally {
      setBuildingTable(false);
    }
  };

  const loadSheet = (wb, sheetName) => {
    const rows = getSheetRows(wb, sheetName);
    setRawRows(rows);
    const det = detectHeader(rows);
    setHeaderRowIndex(det.headerRowIndex);
    setDataStartRowIndex(det.dataStartRowIndex);
    const hdr = rows[det.headerRowIndex] || [];
    setMatchResult(matchColumns(hdr));
  };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const { sheetNames: names, workbook: wb } = parseFileData(buf);
      if (!names.length) { toast({ title: 'No sheets found in file', status: 'error' }); return; }
      setFileName(file.name); setWorkbook(wb); setSheetNames(names); setSelectedSheet(names[0]);
      loadSheet(wb, names[0]);
    } catch (err) {
      toast({ title: 'Could not read file', description: err.message, status: 'error' });
    }
  };

  const onPickSheet = (name) => { setSelectedSheet(name); loadSheet(workbook, name); };

  const reMatch = (hIndex) => {
    setHeaderRowIndex(hIndex);
    setDataStartRowIndex(hIndex + 1);
    const hdr = rawRows[hIndex] || [];
    setMatchResult(matchColumns(hdr));
  };

  const setRowDate = (i, val) => setRowDates((m) => ({ ...m, [i]: val }));
  const applyDateToAll = () => setRowDates(() => {
    const m = {};
    eligibleIndices.forEach((i) => { m[i] = bulkDate; });
    return m;
  });
  const applyDateToChecked = () => setRowDates((m) => {
    const n = { ...m };
    selectedRows.forEach((i) => { n[i] = bulkDate; });
    return n;
  });
  const clearAllDates = () => setRowDates({});
  const toggleRow = (i) => setSelectedRows((s) => {
    const n = new Set(s);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });
  const togglePageSelection = (indices, checked) => setSelectedRows((s) => {
    const n = new Set(s);
    indices.forEach((i) => { if (checked) n.add(i); else n.delete(i); });
    return n;
  });

  const runDupeCheck = async () => {
    setCheckingDupes(true);
    try {
      const keys = await fetchExistingKeys(transformed.rows, effectiveDedupKey, undefined, {
        table: target.table, idColumn: target.dedup.idColumn, idSelect: target.dedup.idSelect,
      });
      const dupes = findExistingDuplicates(transformed.rows, effectiveDedupKey, keys);
      setExistingDupes(dupes);

      // Merge/Replace/Both is only offered when we can read the table's real columns
      // (to tell new columns from changed ones) and upsert on a conflict key. Without
      // that, duplicates are simply skipped — no diff, no prompt.
      if (!canMerge) {
        setExistingRowsByKey(new Map());
        setDuplicateDiff({ newColumns: [], changedColumns: [], changedRowCount: 0, identicalCount: 0 });
        setDuplicateAction('skip');
        return;
      }

      // Fetch the matched rows' current values and classify what the file changes.
      const existingRows = await fetchExistingRows(transformed.rows, effectiveDedupKey, undefined, {
        table: target.table, idColumn: target.dedup.idColumn,
      });
      setExistingRowsByKey(existingRows);

      const extraNames = derivedExtraColumns.map((c) => c.name);
      const desc = { tableColumns: [...columnSet], listColumns: target.listColumns || [], keyColumn: target.onConflict };
      const newCols = new Set();
      const changedCols = new Set();
      let changedRowCount = 0;
      let identicalCount = 0;
      dupes.forEach((i) => {
        const rec = target.toRecord(datedRows[i], extraNames);
        if (!rec) return;
        // Key off the file ROW (same source as detection), not the DB-shaped record —
        // patients_with_ehrid.toRecord emits no dob, so a record-derived name+dob key
        // would never match fetchExistingRows' map.
        const key = keyForRow(datedRows[i], effectiveDedupKey);
        const existingRow = key != null ? existingRows.get(key) : null;
        if (!existingRow) return;
        const { newColumns, changedColumns } = classifyDifferences(existingRow, rec, desc);
        newColumns.forEach((c) => newCols.add(c));
        changedColumns.forEach((c) => changedCols.add(c));
        if (newColumns.length || changedColumns.length) changedRowCount += 1; else identicalCount += 1;
      });
      setDuplicateDiff({
        newColumns: [...newCols], changedColumns: [...changedCols], changedRowCount, identicalCount,
      });
      // Reset to the safe default whenever the detected picture changes.
      setDuplicateAction('skip');
    } catch (err) {
      toast({ title: 'Duplicate check failed', description: err.message, status: 'warning' });
      setExistingDupes(new Set());
      setExistingRowsByKey(new Map());
      setDuplicateDiff({ newColumns: [], changedColumns: [], changedRowCount: 0, identicalCount: 0 });
    } finally {
      setCheckingDupes(false);
    }
  };

  const goToStep = async (n) => {
    if (n === 5) await runDupeCheck();
    setStep(n);
  };

  // Re-run existing-dupe check when the dedup key changes while on Step 5 (I1).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (step === 5) runDupeCheck(); }, [effectiveDedupKey, step, targetTable, canMerge]);

  const finalRows = useMemo(
    () => datedRows.filter((_, i) => eligibleSet.has(i) && !allDupes.has(i)),
    [datedRows, eligibleSet, allDupes],
  );

  // Existing duplicates that will actually be written in pass 2 when an action is
  // chosen. changedRowCount already excludes identical duplicates. Drives the Import
  // button so a pure "update everyone" import (zero brand-new rows) is still runnable.
  const willMergeCount = canMerge && duplicateAction !== 'skip' ? duplicateDiff.changedRowCount : 0;

  const doImport = async () => {
    if (extraColumnError) { toast({ title: extraColumnError, status: 'warning' }); return; }
    setImporting(true);
    setProgress({ done: 0, total: finalRows.length });
    try {
      // Create only genuinely-new columns (user-added ones the live table doesn't
      // already have). deriveMappingInputs computed this set for us; real table
      // columns never need creating. ALTER ... IF NOT EXISTS is idempotent.
      if (newColumnNames.length > 0) {
        await addColumns({
          table: target.table,
          columns: newColumnNames,
          schema: supabaseSchema,
        });
        // Let PostgREST pick up the just-created columns before the first upsert
        // references them (see PGRST_SCHEMA_SETTLE_MS). commitImport still retries
        // schema-cache errors, so a slower-than-usual reload is a delay, not a failure.
        await new Promise((resolve) => setTimeout(resolve, PGRST_SCHEMA_SETTLE_MS));
      }
      const extraNames = derivedExtraColumns.map((c) => c.name);
      const repairOnConflict = async () => {
        toast({
          title: 'Repairing patient table…',
          description: 'One-time rebuild so the import can finish (same as "Build patient tables"). Retrying…',
          status: 'info',
          duration: 6000,
        });
        await runPatientTableJob({ backendUrl: getBackendURL(), schema: supabaseSchema || 'public' });
      };

      // Build merged records for the existing duplicates the user chose to act on.
      // Only rows that actually differ get one — identical duplicates are left alone.
      const mergedRecords = [];
      if (canMerge && duplicateAction !== 'skip') {
        const desc = { tableColumns: [...columnSet], listColumns: target.listColumns || [], keyColumn: target.onConflict };
        existingDupes.forEach((i) => {
          if (!eligibleSet.has(i)) return;
          const rec = target.toRecord(datedRows[i], extraNames);
          if (!rec) return;
          const key = keyForRow(datedRows[i], effectiveDedupKey);
          const existingRow = key != null ? existingRowsByKey.get(key) : null;
          if (!existingRow || existingRow[target.onConflict] == null) return; // need the PK to upsert
          const cls = classifyDifferences(existingRow, rec, desc);
          if (!cls.newColumns.length && !cls.changedColumns.length) return;   // identical -> skip
          mergedRecords.push(buildMergedRecord(existingRow, rec, duplicateAction, {
            ...cls, listColumns: target.listColumns || [], keyColumn: target.onConflict,
          }));
        });
      }

      // TWO-PASS commit. Pass 1: brand-new patients INSERT (ON CONFLICT DO NOTHING) so
      // they can never overwrite an existing row. Pass 2: merged duplicates UPSERT
      // (DO UPDATE) in their OWN batch — required because a merged record carries every
      // existing column (incl. NOT NULL server-managed ones), which would break the
      // new-row inserts if the two were mixed into one heterogeneous batch.
      const res = await commitImport(finalRows, {
        target: targetTable,
        extraColumns: derivedExtraColumns,
        importDuplicates: false,
        onProgress: (done, total) => setProgress({ done, total }),
        repairOnConflict,
      });
      if (mergedRecords.length) {
        const mergedRes = await commitImport([], {
          target: targetTable,
          extraColumns: derivedExtraColumns,
          importDuplicates: true,
          mergedRecords,
          onProgress: (done, total) => setProgress({ done, total }),
          repairOnConflict,
        });
        res.inserted += mergedRes.inserted;
        res.errors = [...res.errors, ...mergedRes.errors];
        res.skipped += mergedRes.skipped;
      }
      setResult(res);
      // Remember which ehr_ids this import touched so the patient-table viewer
      // (SuperAdmin → Patient tables → View table) can highlight the freshly
      // imported rows. Per-schema, localStorage, best-effort.
      if (target.table === 'patients_with_ehrid') {
        const importedKeys = finalRows.map((r) => r.EHR_ID).filter(Boolean).map(String);
        if (importedKeys.length) saveRecentImport(supabaseSchema || 'public', importedKeys);
      }
      if (res.errors.length) {
        res.errors.forEach((e) => console.error('Patient import batch', e.batchIndex, e.message));
        toast({ title: `Imported ${res.inserted}, ${res.errors.length} batch error(s)`, status: 'warning' });
      } else {
        toast({ title: `Imported ${res.inserted} patient(s)`, status: 'success' });
      }
      if (onImported) onImported();
    } catch (err) {
      toast({ title: 'Import failed', description: err.message, status: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const datePageCount = Math.max(1, Math.ceil(eligibleIndices.length / DATE_PAGE_SIZE));
  const datePageClamped = Math.min(datePage, datePageCount - 1);
  const pageStart = datePageClamped * DATE_PAGE_SIZE;
  const pageIndices = eligibleIndices.slice(pageStart, pageStart + DATE_PAGE_SIZE);
  const datesSetCount = finalRows.filter((r) => r.date_).length;

  // Preview of exactly what lands in the table: run the first few final rows
  // through the target's toRecord (same transform commitImport uses), then take
  // the union of produced columns so combined/new columns show up as real cells.
  const previewData = useMemo(() => {
    const extraNames = derivedExtraColumns.map((c) => c.name);
    const recs = [];
    for (const row of finalRows) {
      const rec = target.toRecord(row, extraNames);
      if (rec != null) recs.push(rec);
      if (recs.length >= PREVIEW_LIMIT) break;
    }
    const cols = [];
    const seen = new Set();
    recs.forEach((r) => Object.keys(r).forEach((k) => { if (!seen.has(k)) { seen.add(k); cols.push(k); } }));
    return { recs, cols };
  }, [finalRows, derivedExtraColumns, target]);

  // Before -> after preview for EVERY existing duplicate the chosen action will
  // update (not just one). Mirrors doImport's mergedRecords build so the preview
  // is exactly what gets written: for each row we capture the fields that change,
  // with their old (existing DB) value and new (merged) value. Empty when the
  // action is Skip or nothing actually differs.
  const mergePreview = useMemo(() => {
    if (!canMerge || duplicateAction === 'skip' || !columnSet) return [];
    const extraNames = derivedExtraColumns.map((c) => c.name);
    const desc = { tableColumns: [...columnSet], listColumns: target.listColumns || [], keyColumn: target.onConflict };
    const addNew = duplicateAction === 'merge' || duplicateAction === 'both';
    const doReplace = duplicateAction === 'replace' || duplicateAction === 'both';
    const out = [];
    existingDupes.forEach((i) => {
      if (!eligibleSet.has(i)) return;
      const rec = target.toRecord(datedRows[i], extraNames);
      if (!rec) return;
      const key = keyForRow(datedRows[i], effectiveDedupKey);
      const existingRow = key != null ? existingRowsByKey.get(key) : null;
      if (!existingRow || existingRow[target.onConflict] == null) return;
      const cls = classifyDifferences(existingRow, rec, desc);
      if (!cls.newColumns.length && !cls.changedColumns.length) return;
      const after = buildMergedRecord(existingRow, rec, duplicateAction, {
        ...cls, listColumns: target.listColumns || [], keyColumn: target.onConflict,
      });
      const fields = [];
      if (doReplace) cls.changedColumns.forEach((c) => fields.push({ col: c, kind: 'changed', before: existingRow[c], after: after[c] }));
      if (addNew) cls.newColumns.forEach((c) => fields.push({ col: c, kind: 'new', before: undefined, after: after[c] }));
      if (!fields.length) return;
      out.push({
        key: String(existingRow[target.onConflict]),
        label: existingRow.patient_name || rec.patient_name || existingRow[target.onConflict],
        fields,
      });
    });
    return out;
  }, [canMerge, duplicateAction, columnSet, existingDupes, eligibleSet, datedRows, existingRowsByKey, effectiveDedupKey, derivedExtraColumns, target]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Import patients from CSV / Excel — Step {step} of 7</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {blocked && (
            <Alert status="warning" mb={3}><AlertIcon />Importing is disabled while viewing as another user.</Alert>
          )}

          {step === 1 && (
            <VStack align="stretch" spacing={3}>
              <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                <Text fontSize="sm" fontWeight="medium" mb={1}>Import into</Text>
                <Select value={targetTable} onChange={(e) => setTargetTable(e.target.value)}>
                  {Object.values(TARGET_TABLES).map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}{t.key === DEFAULT_TARGET ? ' — default' : ''}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.600" mt={1}>
                  {target.requiresEhrId
                    ? 'One row per patient, keyed by EHR ID. Phone and visit date are stored as arrays; rows without an EHR ID are skipped.'
                    : 'One row per visit, inserted into notes-c exactly as before.'}
                </Text>
              </Box>
              <Text>Choose a ".csv" or ".xlsx" file exported from your EHR.</Text>
              <Input
                data-testid="patient-import-file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFile}
                p={1}
              />
              {fileName && <Text fontSize="sm" color="gray.600">Loaded: {fileName}</Text>}
              {sheetNames.length > 1 && (
                <Box>
                  <Text fontSize="sm" mb={1}>Sheet</Text>
                  <Select value={selectedSheet} onChange={(e) => onPickSheet(e.target.value)}>
                    {sheetNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </Select>
                </Box>
              )}
            </VStack>
          )}

          {step === 2 && (
            <VStack align="stretch" spacing={3}>
              <HStack>
                <Box>
                  <Text fontSize="sm">Header is row</Text>
                  <Select w="120px" value={headerRowIndex} onChange={(e) => reMatch(Number(e.target.value))}>
                    {rawRows.slice(0, 20).map((_, i) => <option key={i} value={i}>{i + 1}</option>)}
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="sm">Data starts at row</Text>
                  <Select w="120px" value={dataStartRowIndex} onChange={(e) => setDataStartRowIndex(Number(e.target.value))}>
                    {rawRows.slice(0, 25).map((_, i) => <option key={i} value={i}>{i + 1}</option>)}
                  </Select>
                </Box>
              </HStack>
              <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                <Table size="sm">
                  <Tbody>
                    {rawRows.slice(0, 12).map((row, i) => (
                      <Tr key={i} bg={i === headerRowIndex ? 'blue.50' : i >= dataStartRowIndex ? 'green.50' : undefined}>
                        <Td fontWeight="bold">{i + 1}</Td>
                        {(row || []).map((c, j) => <Td key={j}>{c == null ? '' : String(c)}</Td>)}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          )}

          {step === 3 && (
            <VStack align="stretch" spacing={4}>
              {tableMissing ? (
                <Alert status="warning" borderRadius="md" flexDirection="column" alignItems="flex-start" gap={3} p={4}>
                  <HStack align="flex-start" spacing={2}>
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="semibold">The table “{target.table}” doesn’t exist in this schema yet.</Text>
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        {target.requiresEhrId ? PATIENT_TABLE_CONFIRMATION.body : 'Create the table before importing into it.'}
                      </Text>
                    </Box>
                  </HStack>
                  {target.requiresEhrId && (
                    <HStack spacing={2} flexWrap="wrap">
                      <Button colorScheme="blue" size="sm" isLoading={buildingTable} loadingText="Building…" onClick={() => buildPatientTables(false)}>
                        {PATIENT_TABLE_CONFIRMATION.confirmLabel}
                      </Button>
                      <Button variant="outline" colorScheme="teal" size="sm" isDisabled={buildingTable} onClick={() => buildPatientTables(true)}>
                        {PATIENT_TABLE_EMPTY_CONFIRMATION.confirmLabel}
                      </Button>
                    </HStack>
                  )}
                </Alert>
              ) : (<>
              {columnsError && (
                <Alert status="warning" borderRadius="md" flexDirection="column" alignItems="flex-start" gap={1} p={3}>
                  <HStack align="flex-start" spacing={2}>
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="semibold" fontSize="sm">Couldn’t load {target.table}’s columns — showing the standard fields instead.</Text>
                      <Text fontSize="xs" color="gray.600" mt={1}>{columnsError}</Text>
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        You can still map to the standard fields below and import. Creating brand-new columns will fail until the backend can reach the database.
                      </Text>
                    </Box>
                  </HStack>
                </Alert>
              )}
              <HStack justify="space-between" align="baseline">
                <Text fontWeight="semibold">Map your file's columns to {target.table}</Text>
                <Text fontSize="xs" color="gray.500">
                  {columnSet
                    ? `${displayColumns.length} column${displayColumns.length === 1 ? '' : 's'} in ${target.table}`
                    : 'default fields — live columns unavailable'}
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.600">
                Each row is a column in {target.table}. Pick the source column(s) from your file to fill it — press “+” to combine several (e.g. First + Last, or Street + City + State) and choose how they join. Leave a column as “— none —” to skip it.
              </Text>

              <VStack align="stretch" spacing={2}>
                {displayColumns.map(({ col, fieldKey, label, required }) => {
                  const spec = columnCombine[col] || emptySpec();
                  const slots = spec.sources.length ? spec.sources : [null];
                  const activeSources = spec.sources.filter((s) => s != null);
                  const conf = fieldKey && confidence[fieldKey];
                  return (
                    <Box key={col} borderWidth="1px" borderColor="gray.200" borderRadius="md" p={2}>
                      <HStack spacing={2} wrap="wrap" align="center">
                        <Box minW="150px">
                          <Text fontSize="sm" fontWeight="medium">
                            {label}{required && <Text as="span" color="red.500"> *</Text>}
                          </Text>
                          {fieldKey && FIELD_PARSE_HINT[fieldKey] && (
                            <Text fontSize="xs" color="gray.500">parsed as {FIELD_PARSE_HINT[fieldKey]}</Text>
                          )}
                        </Box>
                        <Text fontSize="xs" color="gray.400">←</Text>
                        {slots.map((srcIdx, slot) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <HStack key={slot} spacing={0}>
                            <Select size="sm" w="150px" value={srcIdx == null ? '' : srcIdx}
                              onChange={(e) => setColSource(col, slot, e.target.value)}>
                              <option value="">— none —</option>
                              {columnOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Select>
                            {spec.sources.length > 1 && (
                              <Button size="xs" variant="ghost" colorScheme="red"
                                onClick={() => removeColSource(col, slot)} aria-label="Remove source column">×</Button>
                            )}
                          </HStack>
                        ))}
                        <Button size="xs" variant="outline"
                          onClick={() => addColSource(col)} aria-label="Add another source column">+</Button>
                        {conf && <Badge colorScheme={CONFIDENCE_COLOR[conf]}>{conf}</Badge>}
                      </HStack>
                      {activeSources.length > 1 && (
                        <HStack spacing={2} mt={2} pl={6} wrap="wrap" align="center">
                          <Text fontSize="xs">Combine as</Text>
                          <Select size="sm" w="150px" value={spec.separator}
                            onChange={(e) => setColSeparator(col, e.target.value)}>
                            {SEPARATOR_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                          </Select>
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>Saves as: {previewCombine(spec) || '—'}</Text>
                        </HStack>
                      )}
                    </Box>
                  );
                })}
              </VStack>

              <Divider />
              <Box>
                <HStack justify="space-between" align="baseline" mb={1}>
                  <Text fontWeight="semibold" fontSize="sm">New columns (from unmapped file columns)</Text>
                  <Button size="xs" variant="outline" onClick={addNewColumn}>+ Add column</Button>
                </HStack>
                <Text fontSize="xs" color="gray.600" mb={2}>
                  {newColumns.length > 0
                    ? `File columns that don’t match an existing ${target.table} column are listed here and will be created as text columns on import. Combine, rename, or remove any you don’t want.`
                    : `Any file column that doesn’t match an existing ${target.table} column shows up here to be created. Use “+ Add column” to define one manually.`}
                  {target.table === 'notes-c' && ' Note: new columns are added to the shared notes-c table in your workspace.'}
                </Text>
                {newColumns.length > 0 && (
                  <Text fontSize="xs" color="purple.600" mb={2}>
                    Will create: {newColumnNames.length ? newColumnNames.join(', ') : '—'}
                  </Text>
                )}
                <VStack align="stretch" spacing={2}>
                  {newColumns.map((c) => {
                    const slots = c.sources.length ? c.sources : [null];
                    const activeSources = c.sources.filter((s) => s != null);
                    return (
                      <Box key={c.id} borderWidth="1px" borderColor="purple.200" borderRadius="md" p={2}>
                        <HStack spacing={2} wrap="wrap" align="center">
                          <Input size="sm" w="160px" value={c.name}
                            onChange={(e) => renameNewColumn(c.id, e.target.value)} placeholder="new column name" />
                          <Text fontSize="xs" color="gray.400">←</Text>
                          {slots.map((srcIdx, slot) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <HStack key={slot} spacing={0}>
                              <Select size="sm" w="150px" value={srcIdx == null ? '' : srcIdx}
                                onChange={(e) => setNewSource(c.id, slot, e.target.value)}>
                                <option value="">— none —</option>
                                {columnOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </Select>
                              {c.sources.length > 1 && (
                                <Button size="xs" variant="ghost" colorScheme="red"
                                  onClick={() => removeNewSource(c.id, slot)} aria-label="Remove source column">×</Button>
                              )}
                            </HStack>
                          ))}
                          <Button size="xs" variant="outline"
                            onClick={() => addNewSource(c.id)} aria-label="Add another source column">+</Button>
                          <Text fontSize="xs" color="gray.500">saves as “{sanitizeColumnName(c.name) || '—'}”</Text>
                          <Button size="xs" variant="ghost" colorScheme="red"
                            onClick={() => removeNewColumn(c.id)}>Remove</Button>
                        </HStack>
                        {activeSources.length > 1 && (
                          <HStack spacing={2} mt={2} pl={6} wrap="wrap" align="center">
                            <Text fontSize="xs">Combine as</Text>
                            <Select size="sm" w="150px" value={c.separator}
                              onChange={(e) => setNewSeparator(c.id, e.target.value)}>
                              {SEPARATOR_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </Select>
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>Saves as: {previewCombine(c) || '—'}</Text>
                          </HStack>
                        )}
                      </Box>
                    );
                  })}
                  {newColumns.length === 0 && <Text fontSize="sm" color="gray.500">No new columns.</Text>}
                </VStack>
                {extraColumnError && <Alert status="warning" mt={2}><AlertIcon />{extraColumnError}</Alert>}
              </Box>
              </>)}
            </VStack>
          )}

          {step === 4 && (
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between" wrap="wrap" gap={2}>
                <Text fontWeight="semibold">Review &amp; set visit dates — {eligibleIndices.length} patient(s)</Text>
                <HStack spacing={2}>
                  <Text fontSize="xs" color="gray.600">On unparseable value</Text>
                  <Select size="sm" w="160px" value={onCoerceFail} onChange={(e) => setOnCoerceFail(e.target.value)}>
                    <option value="null">Leave blank</option>
                    <option value="raw">Keep original text</option>
                  </Select>
                </HStack>
              </HStack>
              {excludedNoEhr > 0 && (
                <Text fontSize="sm" color="orange.600">{excludedNoEhr} row(s) excluded — no EHR ID.</Text>
              )}

              <HStack spacing={2} wrap="wrap" bg="gray.50" borderWidth="1px" borderRadius="md" p={2}>
                <Text fontSize="sm" fontWeight="medium">Visit date</Text>
                <Input type="date" size="sm" w="170px" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} />
                <Button size="sm" colorScheme="blue" variant="outline" onClick={applyDateToAll}>
                  Apply to all ({eligibleIndices.length})
                </Button>
                <Button size="sm" variant="outline" onClick={applyDateToChecked} isDisabled={selectedRows.size === 0}>
                  Apply to checked ({selectedRows.size})
                </Button>
                <Button size="sm" variant="ghost" onClick={clearAllDates}>Clear all</Button>
              </HStack>
              <Text fontSize="xs" color="gray.600">
                Edit any row's date directly, or check rows and use "Apply to checked". A patient left with a blank date imports as a roster entry (won't show under a date-filtered schedule).
              </Text>

              <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th w="1%">
                        <Checkbox
                          isChecked={pageIndices.length > 0 && pageIndices.every((i) => selectedRows.has(i))}
                          isIndeterminate={pageIndices.some((i) => selectedRows.has(i)) && !pageIndices.every((i) => selectedRows.has(i))}
                          onChange={(e) => togglePageSelection(pageIndices, e.target.checked)}
                        />
                      </Th>
                      <Th>Patient name</Th>
                      <Th>DOB</Th>
                      <Th>EHR ID</Th>
                      <Th>Visit date</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pageIndices.map((i) => {
                      const r = transformed.rows[i];
                      return (
                        <Tr key={i} bg={selectedRows.has(i) ? 'blue.50' : undefined}>
                          <Td><Checkbox isChecked={selectedRows.has(i)} onChange={() => toggleRow(i)} /></Td>
                          <Td>{r.patient_name}</Td>
                          <Td>{r.dob || ''}</Td>
                          <Td>{r.EHR_ID || ''}</Td>
                          <Td>
                            <Input
                              type="date"
                              size="sm"
                              w="160px"
                              value={rowDates[i] !== undefined ? rowDates[i] : (r.date_ || '')}
                              onChange={(e) => setRowDate(i, e.target.value)}
                            />
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>

              {datePageCount > 1 && (
                <HStack justify="center" spacing={3}>
                  <Button size="xs" onClick={() => setDatePage(Math.max(0, datePageClamped - 1))} isDisabled={datePageClamped === 0}>Prev</Button>
                  <Text fontSize="sm">Rows {pageStart + 1}–{pageStart + pageIndices.length} of {eligibleIndices.length} · Page {datePageClamped + 1}/{datePageCount}</Text>
                  <Button size="xs" onClick={() => setDatePage(Math.min(datePageCount - 1, datePageClamped + 1))} isDisabled={datePageClamped >= datePageCount - 1}>Next</Button>
                </HStack>
              )}
            </VStack>
          )}

          {step === 5 && (
            <VStack align="stretch" spacing={3}>
              <Box borderWidth="1px" borderRadius="md" p={3}>
                <Text>Total data rows scanned: {transformed.stats.total}</Text>
                <Text>Will import: {transformed.stats.imported}</Text>
                <Text>Skipped (no name): {transformed.stats.skippedNoName}</Text>
                <Text>Unparseable — DOB: {transformed.stats.unparseable.dob}, visit date: {transformed.stats.unparseable.date_}, cell phone: {transformed.stats.unparseable.tel_num}, home phone: {transformed.stats.unparseable.home_phone}</Text>
                {excludedNoEhr > 0 && (
                  <Text fontSize="sm" color="orange.600">{excludedNoEhr} row(s) excluded — no EHR ID.</Text>
                )}
              </Box>
              <HStack>
                <Text fontSize="sm">Match duplicates by</Text>
                <Select w="220px" size="sm" value={dedupKey} onChange={(e) => setDedupKey(e.target.value)}>
                  <option value="EHR_ID">EHR ID</option>
                  <option value="name_dob">Name + DOB</option>
                </Select>
                {!isSpecMapped(columnMap.EHR_ID) && dedupKey === 'EHR_ID' && (
                  <Badge colorScheme="orange">EHR ID not mapped — using Name + DOB</Badge>
                )}
                <Button size="sm" onClick={runDupeCheck} isLoading={checkingDupes}>Re-check</Button>
              </HStack>
              <Text>Duplicates found: {allDupes.size} ({inFileDupes.size} in file, {existingDupes.size} already in database)</Text>
              {existingDupes.size > 0 && duplicateDiff.changedRowCount === 0 && (
                <Text fontSize="sm" color="gray.600">
                  {existingDupes.size} already in the database (identical) — they’ll be skipped.
                </Text>
              )}
              {duplicateDiff.changedRowCount > 0 && (
                <Box borderWidth="1px" borderRadius="md" p={3}>
                  <Text fontSize="sm" mb={2}>
                    <b>{duplicateDiff.changedRowCount}</b> of these are already in your database, and your file has updates for them. What should we do?
                  </Text>
                  <RadioGroup value={duplicateAction} onChange={setDuplicateAction}>
                    <VStack align="stretch" spacing={1}>
                      <Radio value="skip">Skip them — only add new patients</Radio>
                      {duplicateDiff.newColumns.length > 0 && (
                        <Radio value="merge">Add new details only</Radio>
                      )}
                      {duplicateDiff.changedColumns.length > 0 && (
                        <Radio value="replace">Update their info</Radio>
                      )}
                      {duplicateDiff.newColumns.length > 0 && duplicateDiff.changedColumns.length > 0 && (
                        <Radio value="both">Do both</Radio>
                      )}
                    </VStack>
                  </RadioGroup>
                  <Text fontSize="xs" color="gray.500" mt={2}>The Preview step shows exactly what changes for each patient.</Text>
                </Box>
              )}
              {allDupes.size > 0 && <Button size="xs" variant="link" onClick={() => setShowDupes((v) => !v)}>{showDupes ? 'Hide' : 'View'} duplicate rows</Button>}
              {showDupes && (
                <Box maxH="200px" overflowY="auto" borderWidth="1px" borderRadius="md">
                  <Table size="sm">
                    <Thead><Tr><Th>Name</Th><Th>DOB</Th><Th>EHR ID</Th><Th>Source</Th></Tr></Thead>
                    <Tbody>
                      {[...allDupes].map((i) => (
                        <Tr key={i}>
                          <Td>{transformed.rows[i].patient_name}</Td>
                          <Td>{transformed.rows[i].dob || ''}</Td>
                          <Td>{transformed.rows[i].EHR_ID || ''}</Td>
                          <Td>{inFileDupes.has(i) ? 'in file' : 'database'}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
              {requiredBlank.length > 0 && (
                <Alert status="info"><AlertIcon />
                  Required fields left blank: {requiredBlank.join(', ')}. They will import as empty.
                </Alert>
              )}
            </VStack>
          )}

          {step === 6 && (
            <VStack align="stretch" spacing={5}>
              {previewData.recs.length > 0 && (
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between" align="baseline" wrap="wrap" gap={2}>
                    <Text fontWeight="semibold">Adding {finalRows.length} new patient(s)</Text>
                    <Text fontSize="xs" color="gray.500">Showing {previewData.recs.length} of {finalRows.length}</Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.600">
                    The actual values that will be written, including combined and newly-added columns. Empty cells import as blank.
                  </Text>
                  {newColumnNames.length > 0 && (
                    <Text fontSize="xs" color="purple.600">New column(s) to be created: {newColumnNames.join(', ')}</Text>
                  )}
                  <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          {previewData.cols.map((c) => (
                            <Th key={c} whiteSpace="nowrap">
                              {c}{newColumnNames.includes(c) && <Text as="span" color="purple.500"> (new)</Text>}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {previewData.recs.map((r, ri) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Tr key={ri}>
                            {previewData.cols.map((c) => (
                              <Td key={c} whiteSpace="nowrap" maxW="240px" isTruncated title={formatPreviewValue(r[c])}>
                                {formatPreviewValue(r[c]) || <Text as="span" color="gray.300">—</Text>}
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </VStack>
              )}

              {mergePreview.length > 0 && (
                <VStack align="stretch" spacing={2}>
                  <Text fontWeight="semibold">Updating {mergePreview.length} existing patient(s)</Text>
                  <Text fontSize="xs" color="gray.600">
                    Old value <Text as="span" color="gray.400">→</Text> new value. List columns (phones, visit dates) are appended — old values are kept.
                  </Text>
                  <VStack align="stretch" spacing={2} maxH="360px" overflowY="auto">
                    {mergePreview.map((row) => (
                      <Box key={row.key} borderWidth="1px" borderRadius="md" p={2} bg="gray.50">
                        <Text fontWeight="medium" fontSize="sm" mb={1}>{row.label}</Text>
                        <VStack align="stretch" spacing={1}>
                          {row.fields.map((f) => (
                            <HStack key={f.col} fontSize="xs" spacing={2} align="baseline" wrap="wrap">
                              <Text minW="130px" color="gray.600" whiteSpace="nowrap">
                                {f.col}{f.kind === 'new' && <Text as="span" color="purple.500"> (new)</Text>}
                              </Text>
                              {f.kind === 'new' ? (
                                <Text color="green.700">{formatPreviewValue(f.after) || <Text as="span" color="gray.300">—</Text>}</Text>
                              ) : (
                                <HStack spacing={2} align="baseline" wrap="wrap">
                                  <Text as="s" color="gray.400">{formatPreviewValue(f.before) || '—'}</Text>
                                  <Text color="gray.400">→</Text>
                                  <Text color="green.700">{formatPreviewValue(f.after) || '—'}</Text>
                                </HStack>
                              )}
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                </VStack>
              )}

              {previewData.recs.length === 0 && mergePreview.length === 0 && (
                <Alert status="info"><AlertIcon />No rows to preview (nothing left to import).</Alert>
              )}
            </VStack>
          )}

          {step === 7 && (
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Ready to import</Text>
              <Text>Insert {finalRows.length} new patient(s) into <Text as="span" fontWeight="semibold">{target.table}</Text>{willMergeCount > 0 ? `, and update ${willMergeCount} existing` : ''}.</Text>
              <Text fontSize="sm" color="gray.600">
                {duplicateAction === 'skip'
                  ? `${allDupes.size} duplicate(s) excluded. `
                  : `${inFileDupes.size} in-file duplicate(s) excluded; ${existingDupes.size} existing duplicate(s) will be merged/updated. `}
                {transformed.stats.skippedNoName} row(s) skipped for missing name.
              </Text>
              {excludedNoEhr > 0 && (
                <Text fontSize="sm" color="orange.600">
                  {excludedNoEhr} row(s) have no EHR ID and will be skipped — {target.table} is keyed by EHR ID.
                </Text>
              )}
              {datesSetCount > 0 && (
                <Text fontSize="sm" color="gray.600">
                  {datesSetCount} of {finalRows.length} will have a visit date set; the rest import as roster entries (no date).
                </Text>
              )}
              {importing && <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} size="sm" />}
              {result && (
                <Alert status={result.errors.length ? 'warning' : 'success'}>
                  <AlertIcon />
                  <Box>
                    <Text>Saved {result.inserted} row(s) to {target.table}.{result.skipped ? ` ${result.skipped} skipped (no EHR ID).` : ''} {result.errors.length} batch error(s).</Text>
                    {result.errors.length > 0 && (
                      <VStack align="start" mt={1} spacing={0}>
                        {result.errors.map((e, ei) => (
                          <Text key={ei} fontSize="xs">Batch {e.batchIndex}: {e.message}</Text>
                        ))}
                      </VStack>
                    )}
                  </Box>
                </Alert>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack w="100%" justify="space-between">
            <Button variant="ghost" onClick={handleClose}>Close</Button>
            <HStack>
              {step > 1 && step <= 7 && !result && <Button onClick={() => setStep(step - 1)} isDisabled={importing}>Back</Button>}
              {step < 7 && (
                <Button colorScheme="blue" onClick={() => goToStep(step + 1)} isDisabled={!rawRows.length || checkingDupes || (step === 3 && tableMissing)} isLoading={checkingDupes && step === 4}>Next</Button>
              )}
              {step === 7 && !result && (
                <Button colorScheme="green" onClick={doImport} isLoading={importing} loadingText="Importing" isDisabled={blocked || (finalRows.length + willMergeCount) === 0 || !!extraColumnError}>{willMergeCount > 0 ? `Import ${finalRows.length} + update ${willMergeCount}` : `Import ${finalRows.length} patients`}</Button>
              )}
              {result && <Button colorScheme="blue" onClick={handleClose}>Done</Button>}
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
